import Vue from 'vue'
import {
  STORE,
  DISCARD,
  PAPERS,
  DISCARD_ALL,
  BIND,
} from '@/store/mutation-types'
import { canonicalizePath } from '@/helpers/canonicalization'
import { classifyActions } from '@/helpers/entities'

function coalesce(...args) {
  return args.filter(arg => arg !== undefined && arg !== null)[0]
}

function bindCache(entity, cache) {
  const forms = entity.forms || []
  forms.forEach(form => {
    const fields = form.fields || []
    fields.forEach(field => {
      if (field.cacheBound)
        return

      field.initialValue = field.value

      const key = `${form.name || 'default'}/${field.name}`
      var cached = cache[key]
      if (!cached) {
        cached = cache[key] = {
          value: coalesce(field.value, field.defaultValue)
        }
      }

      Object.defineProperty(field, 'value', {
        get() { return cached.value },
        set(value) { cached.value = value }
      })

      field.reset = function (options = undefined) {
        if (options !== undefined) {
          field.resetValue = options?.value ?? field.initialValue ?? field.defaultValue
        }
        var value = coalesce(
          field.resetValue, field.initialValue, field.defaultValue)
        Vue.set(field, 'value', value)
      }
      field.clear = function () {
        if (field.readOnly)
          return
        Vue.set(field, 'value', field.defaultValue)
      }
      field.copyFrom = function (source) {
        Vue.set(field, 'value', source[field.name])
      }
      field.copyTo = function (target) {
        Vue.set(target, field.name, field.value)
      }

      field.cacheBound = true
    })
  })
}

const state = {
  bindings: []
}

const mutations = {

  /**
   * Estoca uma entidade obtida do servidor.
   * Em caso de falha um objeto do tipo {Keep.Paper.Design.Fault} pode ser
   * retornado no lugar da entidade real. Objeto do tipo Fault deve ser sempre
   * considerado temporário, existindo apenas enquando a requisição do objeto
   * definivo falha.
   *
   * @param  {Object} state O objeto de estado do Vuex.
   * @param  {Keep.Paper.Design.IEntity} entity A entidade.
   */
  [STORE](state, entity) {
    const path = canonicalizePath(this, entity)

    const cache = state[path]?.cache || entity.cache || {}
    if (entity.cache != cache) {
      entity.cache = cache
      bindCache(entity, cache)
    }

    // Compilando expressões de ações.
    if (['Paper', 'Workspace'].includes(entity.type)) {
      entity.actions = classifyActions(entity)
    }

    // Adicionando a propriedade índice às linhas de conjuntos de dados.
    if (['DataSet'].includes(entity.type) || !!entity.dataSet) {
      const dataSet = entity.dataSet || entity
      const sets = dataSet.sets || []
      sets.forEach(set => {
        const rows = set.rows || []
        rows.forEach((row, index) => row['@index'] = index)
      })
    }

    Vue.set(state, path, entity)
  },

  /**
   * Estoca a ligação entre uma entidade principal e suas entidades vinculadas.
   * As entidades vinculadas são atualizadas quando a entidade principal sofre
   * atualização.
   *
   * @param  {Object} state O objeto de estado do Vuex.
   * @param  {Object} param A ligação, na forma:
   *                        {
   *                          entity: Entity
   *                          observer: Entity
   *                        }
   */
  [BIND](state, param) {
    const { entity, observer } = param

    const path = canonicalizePath(this, entity)
    const observerPath = canonicalizePath(this, observer)

    if (path === observerPath) return

    // console.log({ path, observerPath })

    // const bindings = state.bindings?.[path] || []

    // if (bindings.some(bindingPath => bindingPath === observerPath))
    //   return

    // bindings.push(observerPath)

    // Vue.set(state.bindings, path, bindings)

    // console.log({bindings:state.bindings})
  },

  /**
   * Descarta uma entidade estocada.
   *
   * @param  {} state O objeto de estado do Vuex.
   * @param  {} entityOrPath A entidade ou o seu caminho de estoque.
   */
  [DISCARD](state, entityOrPath) {
    const path = canonicalizePath(this, entityOrPath)
    Vue.delete(state, path)
  },

  /**
   * Descarta todas as entidades estocadas.
   *
   * @param  {} state O objeto de estado do Vuex.
   */
  [DISCARD_ALL](state) {
    Object.keys(state).forEach(path => Vue.delete(state, path));
  },
}

const actions = {
}

const getters = {
  [PAPERS]: state => {

    const isPaper = entity =>
      entity.type === 'Paper' ||
      entity.type === 'Workspace'

    const entries = Object.values(state)
    const entities = entries.filter(e => Object.prototype.hasOwnProperty.call(e, 'eref'))
    const papers = entities.filter(entity => isPaper(entity) && entity.keepOpen)
    return papers
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}